/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import './NotificationsPage.css';

interface Notification {
  id: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'update';
  date: string;
  read: boolean;
}

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadNotifications();
  }, [user]);

  const loadNotifications = async () => {
    try {
      setIsLoading(true);
      
      // Try API first if user is authenticated
      if (isAuthenticated && user?.id) {
        try {
          const apiNotifications = await apiService.getNotifications(user.id);
          if (apiNotifications && apiNotifications.length > 0) {
            // Transform API response to match our Notification interface
            const transformed = apiNotifications.map((notif: any) => ({
              id: String(notif.id || notif.notification_id),
              message: notif.message || notif.title || notif.content || '',
              type: notif.type || 'info',
              date: notif.created_at || notif.date || new Date().toISOString(),
              read: notif.read || notif.is_read || false
            }));
            setNotifications(transformed);
            return;
          }
        } catch (apiError) {
          console.error('API error, falling back to localStorage:', apiError);
        }
      }
      
      // Fallback to localStorage
      const savedNotifications = localStorage.getItem('notifications');
      if (savedNotifications) {
        setNotifications(JSON.parse(savedNotifications));
      } else {
        // Default notifications for first-time users
        const defaultNotifications: Notification[] = [
          {
            id: '1',
            message: 'Welcome to Quickko! 🎉',
            type: 'info',
            date: new Date().toISOString(),
            read: false
          },
          {
            id: '2',
            message: 'Free delivery on orders over ₹500! 🚚',
            type: 'success',
            date: new Date(Date.now() - 86400000).toISOString(),
            read: false
          },
          {
            id: '3',
            message: 'New MDH & Everest spices in stock! 🌶️',
            type: 'update',
            date: new Date(Date.now() - 172800000).toISOString(),
            read: true
          }
        ];
        setNotifications(defaultNotifications);
        localStorage.setItem('notifications', JSON.stringify(defaultNotifications));
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (id: string) => {
    // Optimistic update
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === id ? { ...notif, read: true } : notif
      )
    );

    // Try API first if user is authenticated
    if (isAuthenticated && user?.id) {
      try {
        await apiService.markNotificationAsRead(user.id, id);
        // API call successful, localStorage already synced by API method
        return;
      } catch (error) {
        console.error('Error marking notification as read via API:', error);
        // Fallback to localStorage only
      }
    }

    // Fallback: save to localStorage
    try {
      const updated = notifications.map(notif =>
        notif.id === id ? { ...notif, read: true } : notif
      );
      localStorage.setItem('notifications', JSON.stringify(updated));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const markAllAsRead = async () => {
    // Optimistic update
    const updated = notifications.map(notif => ({ ...notif, read: true }));
    setNotifications(updated);

    // Try API first if user is authenticated
    if (isAuthenticated && user?.id) {
      try {
        await apiService.markAllNotificationsAsRead(user.id);
        // API call successful, localStorage already synced by API method
        return;
      } catch (error) {
        console.error('Error marking all notifications as read via API:', error);
        // Fallback to localStorage only
      }
    }

    // Fallback: save to localStorage
    try {
      localStorage.setItem('notifications', JSON.stringify(updated));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const deleteNotification = async (id: string) => {
    // Optimistic update
    setNotifications(prev => prev.filter(notif => notif.id !== id));

    // Try API first if user is authenticated
    if (isAuthenticated && user?.id) {
      try {
        await apiService.deleteNotification(user.id, id);
        // API call successful, localStorage already synced by API method
        return;
      } catch (error) {
        console.error('Error deleting notification via API:', error);
        // Fallback to localStorage only
      }
    }

    // Fallback: save to localStorage
    try {
      const updated = notifications.filter(notif => notif.id !== id);
      localStorage.setItem('notifications', JSON.stringify(updated));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="notifications-page">
      <div className="notifications-body-container">
        <div className="notifications-header">
          <button className="back-btn" onClick={() => navigate('/app')}>
            ← Back
          </button>
          <h1>Notifications</h1>
          {unreadCount > 0 && (
            <button className="mark-all-read-btn" onClick={markAllAsRead}>
              Mark all as read
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="loading-state">Loading notifications...</div>
        ) : notifications.length === 0 ? (
          <div className="empty-notifications">
            <div className="empty-icon">🔔</div>
            <h2>No notifications</h2>
            <p>You're all caught up! Check back later for updates.</p>
          </div>
        ) : (
          <div className="notifications-list">
            {notifications.map(notification => (
              <div
                key={notification.id}
                className={`notification-item ${notification.type} ${notification.read ? 'read' : 'unread'}`}
              >
                <div className="notification-content">
                  <div className="notification-message">{notification.message}</div>
                  <div className="notification-date">
                    {new Date(notification.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
                <div className="notification-actions">
                  {!notification.read && (
                    <button
                      className="mark-read-btn"
                      onClick={() => markAsRead(notification.id)}
                      aria-label="Mark as read"
                    >
                      ✓
                    </button>
                  )}
                  <button
                    className="delete-btn"
                    onClick={() => deleteNotification(notification.id)}
                    aria-label="Delete notification"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
